import { api } from "@/contexts/AuthContext";

export interface SheetDataResult {
  columns: string[];
  sampleRow: Record<string, any> | null;
}

export type PeriodicityUnitUpper = "MONTHS" | "YEARS" | "DAYS" | "WEEKS";

export const PERIODICITY_PRESETS: { key: string; unit: PeriodicityUnitUpper; value: number }[] = [
  { key: "Diaria", unit: "DAYS", value: 1 },
  { key: "Semanal", unit: "WEEKS", value: 1 },
  { key: "Quincenal", unit: "WEEKS", value: 2 },
  { key: "Mensual", unit: "MONTHS", value: 1 },
  { key: "Trimestral", unit: "MONTHS", value: 3 },
  { key: "Semestral", unit: "MONTHS", value: 6 },
  { key: "Anual", unit: "YEARS", value: 1 },
];

export const CUSTOM_PERIODICITY_MAX: Record<PeriodicityUnitUpper, number> = {
  DAYS: 365,
  WEEKS: 52,
  MONTHS: 12,
  YEARS: 1,
};

const UNIT_LABELS_PLURAL: Record<PeriodicityUnitUpper, string> = {
  DAYS: "días",
  WEEKS: "semanas",
  MONTHS: "meses",
  YEARS: "años",
};

const UNIT_LABELS_SINGULAR: Record<PeriodicityUnitUpper, string> = {
  DAYS: "día",
  WEEKS: "semana",
  MONTHS: "mes",
  YEARS: "año",
};

export function normalizePeriodicityUnit(unit: string | undefined | null): PeriodicityUnitUpper {
  const u = String(unit || "months").toLowerCase();
  if (u === "days" || u === "day" || u === "DAYS") return "DAYS";
  if (u === "weeks" || u === "week" || u === "WEEKS") return "WEEKS";
  if (u === "years" || u === "year" || u === "YEARS") return "YEARS";
  return "MONTHS";
}

export function isPresetPeriodicity(unit: PeriodicityUnitUpper, value: number): boolean {
  return PERIODICITY_PRESETS.some((p) => p.unit === unit && p.value === value);
}

export function getCustomPeriodicityMax(unit: PeriodicityUnitUpper): number {
  return CUSTOM_PERIODICITY_MAX[unit] ?? 1;
}

export function validatePeriodicityValue(
  unit: PeriodicityUnitUpper,
  value: number | null | undefined
): string | null {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "Ingresa un número válido (mínimo 1)";
  }
  const n = Number(value);
  if (!Number.isInteger(n) || n < 1) {
    return "Ingresa un número válido (mínimo 1)";
  }
  const max = getCustomPeriodicityMax(unit);
  if (n > max) {
    return "El máximo permitido es 1 año (365 días, 52 semanas o 12 meses).";
  }
  return null;
}

export function getReminderMaxDays(unit: PeriodicityUnitUpper, value: number): number {
  if (unit === "DAYS") return value;
  if (unit === "WEEKS") return value * 7;
  if (unit === "YEARS") return value * 365;
  return value * 30;
}

export function formatCustomRepeatPreview(unit: PeriodicityUnitUpper, value: number): string {
  if (!value || value < 1 || Number.isNaN(value)) {
    return "Ingresa un intervalo válido";
  }
  if (value === 1) {
    return `Se repetirá cada ${UNIT_LABELS_SINGULAR[unit]}`;
  }
  return `Se repetirá cada ${value} ${UNIT_LABELS_PLURAL[unit]}`;
}

export function formatPeriodicitySelectionLabel(
  unit: PeriodicityUnitUpper,
  value: number,
  isCustom: boolean
): string {
  if (isCustom) {
    return formatCustomRepeatPreview(unit, value).replace(/^Se repetirá /, "").replace(/^cada/, "Cada");
  }
  const preset = PERIODICITY_PRESETS.find((p) => p.unit === unit && p.value === value);
  if (preset) return preset.key;
  return formatCustomRepeatPreview(unit, value).replace(/^Se repetirá /, "").replace(/^cada/, "Cada");
}

export function formatPeriodicityDisplay(unit: PeriodicityUnitUpper, value: number): string {
  if (!value) return "—";
  if (unit === "DAYS") {
    return value === 1 ? "Diaria" : `Cada ${value} días`;
  }
  if (unit === "WEEKS") {
    if (value === 1) return "Semanal";
    if (value === 2) return "Quincenal";
    return `Cada ${value} semanas`;
  }
  if (unit === "MONTHS") {
    if (value === 1) return "Mensual";
    if (value === 3) return "Trimestral";
    if (value === 6) return "Semestral";
    if (value === 12) return "Anual";
    return `Cada ${value} mes${value > 1 ? "es" : ""}`;
  }
  return value === 1 ? "Anual" : `Cada ${value} año${value > 1 ? "s" : ""}`;
}

export const parseReminderDays = (reminderDays: number[]): number[] =>
  Array.from(new Set(reminderDays.filter(n => Number.isInteger(n) && n >= 0))).sort((a, b) => b - a);

export const fetchSheetData = async (
  sheetId: string,
  sheetName?: string,
  sheetTabId?: number | null
): Promise<SheetDataResult | null> => {
  if (!sheetId) return null;

  try {
    const res = await api.get(`/google-sheets/${sheetId}/data`, {
      params: {
        max_rows: 3,
        ...(sheetName ? { sheet_name: sheetName } : {}),
        ...(sheetTabId != null ? { google_sheet_tab_id: sheetTabId } : {}),
      },
    });

    const data = res?.data || {};
    
    console.log('📊 Raw sheet data from API:', data);
    
    let cols: string[] = [];
    let sampleRow: Record<string, any> | null = null;
    let rows: any[] = [];

    let headerInFirstRow = false;

    // Extract columns - prioritize 'columns' field from backend
    if (Array.isArray(data.columns) && data.columns.length) {
      cols = data.columns.map(String);
      console.log('📊 Using data.columns:', cols);
    } else if (Array.isArray(data.headers) && data.headers.length) {
      cols = data.headers.map(String);
      console.log('📊 Using data.headers:', cols);
    } else if (Array.isArray(data.values) && data.values.length) {
      const first = data.values[0];
      if (Array.isArray(first)) {
        cols = first.map((c: any, i: number) => (c ?? `Column ${i + 1}`).toString());
        headerInFirstRow = true;
        console.log('📊 Using data.values[0]:', cols);
      }
      else if (first && typeof first === "object") {
        cols = Object.keys(first);
        console.log('📊 Using Object.keys(data.values[0]):', cols);
      }
    } else if (Array.isArray(data.rows) && data.rows.length) {
      const first = data.rows[0];
      if (Array.isArray(first)) {
        cols = first.map((c: any, i: number) => (c ?? `Column ${i + 1}`).toString());
        headerInFirstRow = true;
        console.log('📊 Using data.rows[0]:', cols);
      }
      else if (first && typeof first === "object") {
        cols = Object.keys(first);
        console.log('📊 Using Object.keys(data.rows[0]):', cols);
      }
    }

    // Don't deduplicate - keep all columns as they come from the API
    console.log('📊 Final columns:', cols);

    // Extract rows/sample data
    if (Array.isArray(data.rows)) rows = data.rows;
    else if (Array.isArray(data.values)) {
      rows = data.values;
    }

    // Attempt to get a sample row
    if (rows && rows.length > 0) {
      if (typeof rows[0] === 'object' && rows[0] !== null && !Array.isArray(rows[0])) {
        sampleRow = rows[0];
      }
      else if (Array.isArray(rows[0])) {
        let startIndex = 0;
        if (headerInFirstRow && rows.length > 1) {
          startIndex = 1;
        }

        if (rows[startIndex] && Array.isArray(rows[startIndex])) {
          const rowVals = rows[startIndex];
          sampleRow = {};
          cols.forEach((col, idx) => {
            // @ts-ignore
            sampleRow[col] = rowVals[idx];
          });
        }
      }
    }

    return { columns: cols, sampleRow };
  } catch (err) {
    console.error("No se pudieron cargar datos de la hoja:", err);
    return null;
  }
};

// Deprecated wrapper for backward compatibility
export const fetchSheetColumns = async (
  sheetId: string,
  sheetName?: string
): Promise<string[] | null> => {
  const res = await fetchSheetData(sheetId, sheetName);
  return res ? res.columns : null;
}

export const selectMostRecentSheet = (sheets: any[]): string | null => {
  if (!Array.isArray(sheets) || sheets.length === 0) return null;
  const pickDate = (s: any) => {
    const d = s.last_modified || s.updated_at || s.created_at || null;
    return d ? new Date(d).getTime() : 0;
  };
  const mostRecent = sheets.reduce((best: any, s: any) => (pickDate(s) > pickDate(best) ? s : best), sheets[0]);
  return mostRecent?.sheet_id ?? mostRecent?.id ?? mostRecent?._id ?? null;
};

export const autoSelectColumns = (cols: string[], phoneColumn: string, renewalDateColumn: string): { phone: string; date: string } => {
  const lc = (s: string) => s.toLowerCase();
  const phoneCandidates = ["telefono", "teléfono", "phone", "celular", "mobile", "tel"];
  const dateCandidates = ["fecha", "vencimiento", "fecha_vencimiento", "due_date", "renewal", "fecha_renovacion", "renewal_date"];

  let selectedPhone = phoneColumn;
  let selectedDate = renewalDateColumn;

  if (!selectedPhone) {
    const found = cols.find(c => phoneCandidates.includes(lc(c)));
    if (found) selectedPhone = found;
  }
  if (!selectedDate) {
    const found = cols.find(c => dateCandidates.some(dc => lc(c).includes(dc)));
    if (found) selectedDate = found;
  }

  return { phone: selectedPhone, date: selectedDate };
};